// Taktart für **einen** Takt ändern (Takt-Editor „Taktart", Schalter „Noten neu aufteilen" aus).
// Eigenes Modul, weil es mehr ist als `<time>` zu schreiben: der Folgetakt bekommt die alte Taktart
// zurück, und der Inhalt des Takts wird in jeder Stimme auf das neue Metrum gebracht (auffüllen bzw.
// kürzen). Das Neuaufteilen über mehrere Takte macht der Server (`POST /api/rebar`).
// Abhängigkeitsregel wie im ganzen Ordner: Basis-Helfer aus `core.ts`, gerichtete Geschwister-Imports
// (structure, notes, durations, measureAttributes, measureRuns), keine Zyklen.
import {
    findMeasureInPartByIndex,
    getPartDivisions,
    getParts,
    parseMusicXml,
    serializeMusicXml,
    ticksToTypeAndDots,
    voiceOf,
} from './core';
import {decomposeTicks} from './durations';
import {expectedMeasureTicks} from './measureAttributes';
import {type Run, adjustBackup, padRun, shortenRest, splitRuns, tagOf, timedTicks} from './measureRuns';
import {addTiedNotation, hasTie, insertTieElement, removeIncomingBows, removeOutgoingBows, removeTieEnds} from './notes';
import {applyMeasureAttrs, injectCascadeAttr, setDurationTypeAndDots} from './structure';

export interface SetMeasureMeterResult {
    /** Neues MusicXML; `null`, wenn sich der Takt nicht anpassen ließ (Grund in `message`). */
    xml: string | null;
    /** Rückmeldung für den Nutzer (Toast). */
    message: string;
}

interface Meter { beats: number; beatType: number }

const meterOf = (time: Element | undefined): Meter => ({
    beats: parseInt(time?.querySelector('beats')?.textContent || '4', 10) || 4,
    beatType: parseInt(time?.querySelector('beat-type')?.textContent || '4', 10) || 4,
});

const sameMeter = (a: Meter, b: Meter) => a.beats === b.beats && a.beatType === b.beatType;

/** Das eigene `<time>` eines Takts (nicht das kaskadierte). */
function ownTime(measure: Element): Element | null {
    for (const attrs of measure.querySelectorAll('attributes')) {
        const time = [...attrs.children].find(c => tagOf(c) === 'time');
        if (time) return time;
    }
    return null;
}

/** Entfernt das eigene `<time>` eines Takts samt eines dadurch leeren `<attributes>`. */
function removeOwnTime(measure: Element): void {
    const time = ownTime(measure);
    if (!time) return;
    const attrs = time.parentElement!;
    time.remove();
    if (!attrs.children.length) attrs.remove();
}

/** Baut ein `<time>`-Element (für {@link injectCascadeAttr}). */
function buildTime(doc: Document, meter: Meter): Element {
    const time = doc.createElement('time');
    const beats = doc.createElement('beats');
    beats.textContent = String(meter.beats);
    const beatType = doc.createElement('beat-type');
    beatType.textContent = String(meter.beatType);
    time.append(beats, beatType);
    return time;
}

/** Setzt das eigene `<time>` eines Takts (legt es in canonical Reihenfolge an, falls es fehlt). Ein
 *  `symbol` (C, ¢) passt nach einem Wechsel nicht mehr zum Bruch und entfällt. */
function writeOwnTime(doc: Document, measure: Element, meter: Meter): void {
    const time = ownTime(measure);
    if (!time) {
        injectCascadeAttr(doc, measure, 'time', buildTime(doc, meter));
        return;
    }
    time.removeAttribute('symbol');
    for (const tag of ['beats', 'beat-type'] as const) {
        let el = [...time.children].find(c => tagOf(c) === tag);
        if (!el) {
            el = doc.createElement(tag);
            time.appendChild(el);
        }
        el.textContent = String(tag === 'beats' ? meter.beats : meter.beatType);
    }
}

/** Primärnote samt `<chord/>`-Begleitern (die Begleiter folgen ihr direkt im Abschnitt). */
interface Group { elements: Element[]; onset: number; ticks: number }

/** Zerlegt einen Abschnitt in seine Zählzeiten (Primärnote + Begleiter, Pause, `<forward>`) mit
 *  Onset. Vorschläge hängen an der folgenden Zählzeit; Akkordsymbole/Directions bleiben außen vor. */
function groupsOf(run: Run): Group[] {
    const groups: Group[] = [];
    let pendingGrace: Element[] = [];
    let cursor = 0;
    for (const el of run.elements) {
        const tag = tagOf(el);
        if (tag === 'note' && el.querySelector('grace')) {
            pendingGrace.push(el);
            continue;
        }
        if (tag === 'note' && el.querySelector('chord')) {
            groups[groups.length - 1]?.elements.push(el);
            continue;
        }
        const t = timedTicks(el);
        if (!t) continue;
        groups.push({elements: [...pendingGrace, el], onset: cursor, ticks: t});
        pendingGrace = [];
        cursor += t;
    }
    if (pendingGrace.length) groups.push({elements: pendingGrace, onset: cursor, ticks: 0});
    return groups;
}

const isSounding = (el: Element) => tagOf(el) === 'note' && !el.querySelector('rest') && !el.querySelector('grace');

/** Kürzt eine klingende Zählzeit auf `ticks` – als eine Note, wenn schreibbar, sonst als gebundene
 *  Kette (erstes Stück = Original mit Gesangstext/Artikulation, weitere Stücke bereinigte Klone).
 *  Ein ausgehender Haltebogen wandert ans letzte Stück. `false`, wenn `ticks` nicht schreibbar ist. */
function shortenSoundingGroup(doc: Document, group: Group, ticks: number, divisions: number): boolean {
    const notes = group.elements.filter(isSounding);
    const shapes = decomposeTicks(ticks, divisions);
    if (!shapes) return false;
    const outgoing = notes.map(n => hasTie(n, 'start'));
    for (const n of notes) {
        // Eine gekürzte Triolen-Note ist keine Triolen-Note mehr.
        for (const el of [...n.querySelectorAll(':scope > time-modification, tuplet')]) el.remove();
        for (const beam of [...n.querySelectorAll('beam')]) beam.remove();
    }
    let previous = notes;
    let anchor = notes[notes.length - 1]!;
    for (const [k, shape] of shapes.entries()) {
        if (k === 0) {
            for (const n of notes) {
                removeTieEnds(n, 'start');
                setDurationTypeAndDots(doc, n, shape.ticks, shape.type, shape.dots);
            }
            continue;
        }
        const clones = notes.map(n => {
            const clone = n.cloneNode(true) as Element;
            for (const el of [...clone.querySelectorAll('lyric, beam, tie, notations, accidental')]) el.remove();
            setDurationTypeAndDots(doc, clone, shape.ticks, shape.type, shape.dots);
            return clone;
        });
        previous.forEach(n => {
            insertTieElement(doc, n, 'start');
            addTiedNotation(doc, n, 'start');
        });
        clones.forEach(c => {
            insertTieElement(doc, c, 'stop');
            addTiedNotation(doc, c, 'stop');
        });
        anchor.after(...clones);
        anchor = clones[clones.length - 1]!;
        previous = clones;
    }
    previous.forEach((n, i) => {
        if (!outgoing[i]) return;
        insertTieElement(doc, n, 'start');
        addTiedNotation(doc, n, 'start');
    });
    return true;
}

/**
 * Kürzt einen Abschnitt auf `limit` Ticks: Zählzeiten ab `limit` entfallen (ihre Bögen werden an
 * beiden Enden gelöst), die Zählzeit über `limit` wird gekürzt. Liefert die Zahl entfernter
 * klingender Zählzeiten, oder `null`, wenn die Restlänge nicht schreibbar ist.
 */
function truncateRun(doc: Document, part: Element, run: Run, limit: number, divisions: number): number | null {
    let removed = 0;
    for (const group of groupsOf(run)) {
        const end = group.onset + group.ticks;
        if (end <= limit && !(group.ticks === 0 && group.onset >= limit)) continue;
        if (group.onset >= limit) {
            if (group.elements.some(isSounding)) removed++;
            for (const el of group.elements) {
                if (tagOf(el) === 'note') {
                    removeIncomingBows(part, el, voiceOf(el));
                    removeOutgoingBows(part, el, voiceOf(el));
                }
                el.remove();
            }
            continue;
        }
        // Zählzeit ragt über die neue Taktlinie.
        const keep = limit - group.onset;
        const primary = group.elements.find(el => timedTicks(el) > 0)!;
        if (!group.elements.some(isSounding)) {
            shortenRest(doc, primary, keep, divisions);
        } else if (!shortenSoundingGroup(doc, group, keep, divisions)) {
            return null;
        }
    }
    adjustBackup(run, limit - run.length);
    return removed;
}

/** Eine ganztaktige Pause (`<rest measure="yes">`) – sie wird nur umgestellt, nicht zerlegt. */
function measureRestOf(run: Run): Element | null {
    const timed = run.elements.filter(el => timedTicks(el) > 0);
    const only = timed.length === 1 ? timed[0]! : null;
    return only?.querySelector('rest')?.getAttribute('measure') === 'yes' ? only : null;
}

/**
 * Ändert die Taktart **nur des Takts** an `measureIndex` (0-basiert, in allen Parts):
 *
 * - **Nur dieser Takt:** sein `<time>` wird gesetzt, der Folgetakt bekommt die bisher geltende
 *   Taktart als eigenes `<time>` zurück (hat er schon eines, bleibt es). Danach überflüssige
 *   `<time>` (gleich der vorher geltenden) entfernt – sonst zeigte ein Hin-und-zurück doppelte
 *   Taktangaben.
 * - **Inhalt passt sich an**, je Stimme/Staff: zu kurz → Pausen am Ende; zu lang → Zählzeiten ab der
 *   neuen Taktlinie entfallen, die überstehende wird gekürzt (nicht schreibbare Längen als gebundene
 *   Kette). So bleibt kein Takt überfüllt – ein überfüllter Takt ließe den Export mit
 *   `MeterException` scheitern. Ein als Auftakt markierter Takt (`implicit="yes"`) wird nicht
 *   aufgefüllt, eine Ganztaktpause nur umgestellt.
 *
 * `xml: null` mit Grund in `message`, wenn sich ein Takt nicht anpassen lässt (Stimme beginnt nicht
 * am Taktanfang, Restlänge nicht schreibbar) oder der Takt nicht existiert.
 */
export function setMeasureMeterInMusicXml(
    musicXml: string, measureIndex: number, beats: number, beatType: number,
): SetMeasureMeterResult {
    const meter: Meter = {beats, beatType};
    const doc = parseMusicXml(musicXml);
    let removed = 0;
    let padded = false;
    let found = false;

    for (const part of getParts(doc)) {
        const measures = [...part.querySelectorAll('measure')];
        const measure = findMeasureInPartByIndex(part, measureIndex);
        if (!measure) continue;
        found = true;

        // Kaskade vor dem Eingriff: geltend VOR dem Takt und IM Takt.
        const state = new Map<string, Element>();
        for (let i = 0; i < measureIndex; i++) applyMeasureAttrs(measures[i]!, state);
        const before = meterOf(state.get('time'));
        const hadTimeBefore = state.has('time');
        applyMeasureAttrs(measure, state);
        const old = meterOf(state.get('time'));
        const divisions = parseInt(state.get('divisions')?.textContent || '', 10) || getPartDivisions(part) || 1;

        // Inhalt zuerst – die Runs zeigen auf die unveränderten Elemente.
        const runs = splitRuns(measure);
        if (!runs) {
            return {xml: null, message: `Takt ${measure.getAttribute('number') ?? measureIndex + 1} hat eine Stimmenstruktur, die sich nicht automatisch anpassen lässt`};
        }
        const expected = expectedMeasureTicks(beats, beatType, divisions);
        const isPickup = measure.getAttribute('implicit') === 'yes';
        for (const run of runs) {
            const measureRest = measureRestOf(run);
            if (measureRest) {
                measureRest.querySelector('duration')!.textContent = String(expected);
                adjustBackup(run, expected - run.length);
                continue;
            }
            if (run.length > expected) {
                const count = truncateRun(doc, part, run, expected, divisions);
                if (count === null) {
                    return {xml: null, message: 'Die gekürzte Note lässt sich nicht als Notenwert schreiben'};
                }
                removed += count;
            } else if (run.length < expected && !isPickup) {
                padRun(doc, run, expected - run.length, divisions, true);
                padded = true;
            }
        }

        // Taktart: nur dieser Takt.
        const next = measures[measureIndex + 1];
        if (next && !ownTime(next) && !sameMeter(old, meter)) {
            injectCascadeAttr(doc, next, 'time', buildTime(doc, old));
        }
        writeOwnTime(doc, measure, meter);
        if (measureIndex > 0 && hadTimeBefore && sameMeter(before, meter)) removeOwnTime(measure);
        const nextTime = next ? ownTime(next) : null;
        if (next && nextTime && sameMeter(meterOf(nextTime), meter)) removeOwnTime(next);
    }

    if (!found) return {xml: null, message: 'Takt nicht gefunden'};

    const label = `${beats}/${beatType}`;
    const effects = [
        removed ? (removed === 1 ? '1 Note am Taktende entfernt' : `${removed} Noten am Taktende entfernt`) : '',
        padded ? 'mit Pausen aufgefüllt' : '',
    ].filter(Boolean);
    return {
        xml: serializeMusicXml(doc),
        message: `Taktart ${label} nur für diesen Takt${effects.length ? ` – ${effects.join(', ')}` : ''}`,
    };
}
